import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/ApiConfig';
import { isAdminAuthorized, isSuperAdmin } from '../utils/userUtils';
import Layout from './components/Layout';
import ResponsiveShadow from './components/ResponsiveShadow';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import DefaultModal from './components/modals/DefaultModal';
import LoadingModal from './components/modals/LoadingModal';
import DataPreviewModal from './components/modalServices/DataPreviewModal';

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function getAuthConfig() {
  return { headers: getAuthHeaders(), suppressAuthRedirect: true };
}

const POLL_INTERVAL = 5000;

function CatmatImport() {
  const [isStarting, setIsStarting] = useState(false);
  const [job, setJob] = useState(null);
  const [success, setSuccess] = useState('');
  const [importStats, setImportStats] = useState(null);
  const [user, setUser] = useState(null);
  const [usuarioBind, setUsuarioBind] = useState(null);

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [statsError, setStatsError] = useState('');

  const [previewData, setPreviewData] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  const pollTimeoutRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    const usuarioBindData = localStorage.getItem('vinculo');

    if (!userData) {
      navigate('/');
      return;
    }

    const parsedUser = JSON.parse(userData);
    if (!usuarioBindData) {
      navigate('/seleciona_entidade');
      return;
    }
    const parsedUsuarioBind = JSON.parse(usuarioBindData);
    setUser(parsedUser);
    setUsuarioBind(parsedUsuarioBind);

    if (!token && !isAdminAuthorized(parsedUser, parsedUsuarioBind)) {
      navigate('/home');
      return;
    }

    loadStats();
  }, [token, navigate]);

  const loadStats = async () => {
    try {
      const statsResponse = await axios.get(
        `${API_BASE_URL.CATMAT}/estatisticas`,
        getAuthConfig()
      );
      setImportStats(statsResponse.data);
      setStatsError('');
    } catch (err) {
      setStatsError(
        'Não foi possível carregar as estatísticas. Tente novamente.'
      );
    }
  };

  const previewColumns = [
    { key: 'codigoItem', label: 'Código' },
    { key: 'descricaoItem', label: 'Descrição' },
    { key: 'codigoGrupo', label: 'Grupo' },
    { key: 'codigoClasse', label: 'Classe' },
    { key: 'codigoPdm', label: 'PDM' },
    { key: 'statusItem', label: 'Situação' },
  ];

  const handlePreview = async () => {
    setIsPreviewLoading(true);
    setShowPreviewModal(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL.CATMAT}/primeiros`,
        getAuthConfig()
      );
      setPreviewData(response.data);
    } catch (err) {
      setPreviewData([]);
      setErrorMessage('Erro ao carregar registros para visualização.');
      setShowErrorModal(true);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const startPolling = jobId => {
    if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);

    let pollErrors = 0;
    const MAX_POLL_ERRORS = 3;

    const poll = async () => {
      try {
        const statusResp = await axios.get(
          `${API_BASE_URL.CATMAT}/job/${jobId}/status`,
          getAuthConfig()
        );

        if (!mountedRef.current) return;

        const jobStatus = statusResp.data;
        if (!jobStatus) {
          pollErrors += 1;
          if (pollErrors >= MAX_POLL_ERRORS) {
            setErrorMessage('Erro ao consultar status do job');
            setShowErrorModal(true);
          } else {
            pollTimeoutRef.current = setTimeout(poll, POLL_INTERVAL);
          }
          return;
        }

        pollErrors = 0;
        setJob(jobStatus);

        if (jobStatus.status === 'RUNNING' || jobStatus.status === 'PENDING') {
          pollTimeoutRef.current = setTimeout(poll, POLL_INTERVAL);
        } else if (jobStatus.status === 'SUCCESS') {
          setSuccess(
            `Importação concluída: ${jobStatus.totalImported} registros importados.`
          );
          loadStats();
        } else if (jobStatus.status === 'FAILED') {
          setErrorMessage(
            `Importação falhou: ${jobStatus.message || 'Erro desconhecido'}`
          );
          setShowErrorModal(true);
        } else if (jobStatus.status === 'NOT_FOUND') {
          setErrorMessage(
            'Importação não encontrada no servidor (pode ter sido interrompida por uma reinicialização). Inicie a importação novamente.'
          );
          setShowErrorModal(true);
        }
      } catch (pollErr) {
        console.error('Erro ao consultar status do job:', pollErr);
        if (!mountedRef.current) return;
        pollErrors += 1;
        if (pollErrors >= MAX_POLL_ERRORS) {
          setErrorMessage(
            'Erro ao consultar status do job. Verifique se o servidor está ativo e tente novamente.'
          );
          setShowErrorModal(true);
        } else {
          pollTimeoutRef.current = setTimeout(poll, POLL_INTERVAL);
        }
      }
    };

    pollTimeoutRef.current = setTimeout(poll, POLL_INTERVAL);
  };

  const handleFullImport = async () => {
    if (!isSuperAdmin(user)) {
      setErrorMessage(
        'Acesso negado. Você não possui permissão para executar esta ação.'
      );
      setShowErrorModal(true);
      return;
    }

    setIsStarting(true);
    setSuccess('');
    setJob(null);

    try {
      const startResp = await axios.post(
        `${API_BASE_URL.CATMAT}/importar-todos-async`,
        {},
        getAuthConfig()
      );

      if (startResp.status === 202 && startResp.data && startResp.data.jobId) {
        setJob({
          jobId: startResp.data.jobId,
          status: 'PENDING',
          message: 'Importação iniciada em background',
          totalImported: 0,
          currentPage: 0,
          totalPages: null,
        });
        startPolling(startResp.data.jobId);
      } else {
        setErrorMessage(
          startResp.data?.message || 'Não foi possível iniciar a importação'
        );
        setShowErrorModal(true);
      }
    } catch (err) {
      console.error('Erro ao iniciar importação assíncrona:', err);
      setErrorMessage(
        err.response?.data?.message || 'Erro ao iniciar importação'
      );
      setShowErrorModal(true);
    } finally {
      setIsStarting(false);
    }
  };

  const refreshStats = () => {
    loadStats();
  };

  const formatDateTime = dateTime => {
    if (!dateTime) return 'N/A';

    try {
      let date;
      if (dateTime instanceof Date) {
        date = dateTime;
      } else if (typeof dateTime === 'number') {
        const ms = dateTime < 1e12 ? dateTime * 1000 : dateTime;
        date = new Date(ms);
      } else if (typeof dateTime === 'string') {
        date = new Date(dateTime);
      } else if (Array.isArray(dateTime)) {
        date = new Date(
          dateTime[0],
          (dateTime[1] || 1) - 1,
          dateTime[2] || 1,
          dateTime[3] || 0,
          dateTime[4] || 0,
          dateTime[5] || 0
        );
      } else {
        return 'N/A';
      }

      if (isNaN(date.getTime())) {
        return 'Data inválida';
      }

      return date.toLocaleString('pt-BR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch (error) {
      return 'Erro na data';
    }
  };

  const formatNumber = number => {
    if (typeof number !== 'number') return number;
    return number.toLocaleString('pt-BR');
  };

  const isJobActive = job && (job.status === 'PENDING' || job.status === 'RUNNING');
  const jobPercent =
    job && job.totalPages
      ? Math.min(100, Math.round((job.currentPage * 100) / job.totalPages))
      : null;

  const statusBadge = status => {
    switch (status) {
      case 'PENDING':
        return <span className="badge bg-warning text-dark"><i className="bi bi-hourglass-split me-1"></i>Aguardando</span>;
      case 'RUNNING':
        return <span className="badge bg-primary"><i className="bi bi-arrow-repeat me-1"></i>Em execução</span>;
      case 'SUCCESS':
        return <span className="badge bg-success"><i className="bi bi-check-circle me-1"></i>Concluída</span>;
      case 'FAILED':
        return <span className="badge bg-danger"><i className="bi bi-x-circle me-1"></i>Falhou</span>;
      default:
        return <span className="badge bg-secondary">{status || 'Desconhecido'}</span>;
    }
  };

  if (!user && !showErrorModal) {
    return (
      <Layout>
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="card shadow-sm">
                <div className="card-body text-center">
                  <div className="alert alert-danger" role="alert">
                    <h4 className="alert-heading">Acesso Negado</h4>
                    <p>
                      Você não possui permissão para acessar esta
                      funcionalidade.
                    </p>
                    <p className="mb-0">
                      Apenas administradores autorizados podem importar dados
                      CATMAT.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <ResponsiveShadow>
        <PageHeader title="Importação CATMAT" icon="bi-cloud-download" />
        <PageContent>
          {/* Estatísticas dos Dados */}
          {statsError && (
            <div
              className="alert alert-warning alert-dismissible fade show d-flex align-items-center gap-2"
              role="alert"
            >
              <i className="bi bi-exclamation-triangle-fill"></i>
              <span>{statsError}</span>
              <button
                type="button"
                className="btn-close"
                onClick={() => setStatsError('')}
              ></button>
            </div>
          )}

          {importStats && (
            <div className="row g-3 mb-3">
              <div className="col-md-6 col-xl-4">
                <div className="card border-0 shadow-sm">
                  <div className="card-body d-flex align-items-center gap-3">
                    <div
                      className="rounded-3 d-flex align-items-center justify-content-center"
                      style={{
                        width: '56px',
                        height: '56px',
                        background: 'linear-gradient(45deg, rgb(19, 72, 156) 0%, rgba(50, 120, 220, 1) 100%)',
                        color: '#fff',
                        flexShrink: 0,
                      }}
                    >
                      <i className="bi bi-database fs-3"></i>
                    </div>
                    <div className="min-w-0">
                      <div className="text-muted small text-uppercase" style={{ fontSize: '0.75rem' }}>
                        Total de Registros
                      </div>
                      <div className="fs-3 fw-bold text-primary">
                        {formatNumber(importStats.totalRegistros)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-6 col-xl-4">
                <div className="card border-0 shadow-sm">
                  <div className="card-body d-flex align-items-center gap-3">
                    <div
                      className="rounded-3 d-flex align-items-center justify-content-center"
                      style={{
                        width: '56px',
                        height: '56px',
                        background: 'linear-gradient(45deg, #0d6efd 0%, #0dcaf0 100%)',
                        color: '#fff',
                        flexShrink: 0,
                      }}
                    >
                      <i className="bi bi-calendar-check fs-3"></i>
                    </div>
                    <div className="min-w-0">
                      <div className="text-muted small text-uppercase" style={{ fontSize: '0.75rem' }}>
                        Última Atualização
                      </div>
                      <div className="fs-6 fw-semibold" style={{ color: '#13489c' }}>
                        {formatDateTime(importStats.ultimaAtualizacao)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Seção de Importação Completa */}
          <div className="card border-0 shadow-sm">
            <div
              className="card-header text-white"
              style={{
                background: 'linear-gradient(45deg, rgb(19, 72, 156) 0%, rgba(19, 72, 156, 1) 100%)',
              }}
            >
              <h5 className="mb-0">
                <i className="bi bi-cloud-download me-2"></i>
                Importação de Dados CATMAT
              </h5>
            </div>
            <div className="card-body">
              <p className="text-muted">
                Importa todos os dados disponíveis na API CATMAT do Governo
                Federal. A operação é executada em segundo plano e o progresso
                é exibido abaixo.
              </p>
              <div className="d-flex flex-wrap gap-2">
                <button
                  className="btn btn-primary"
                  onClick={handleFullImport}
                  disabled={isStarting || isJobActive}
                >
                  <i className="bi bi-cloud-arrow-down me-2"></i>
                  Importar Dados
                </button>
                <button
                  className="btn btn-outline-primary"
                  onClick={refreshStats}
                  disabled={isStarting || isJobActive}
                >
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Atualizar Estatísticas
                </button>
                <button
                  className="btn btn-outline-success"
                  onClick={handlePreview}
                  disabled={isStarting || isJobActive}
                >
                  <i className="bi bi-eye me-2"></i>
                  Visualizar
                </button>
              </div>
            </div>
          </div>

          {/* Progresso do job assíncrono */}
          {job && (
            <div className="card border-0 shadow-sm mt-3">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
                  <div className="d-flex align-items-center gap-2">
                    {isJobActive ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Carregando...</span>
                        </span>
                        <strong className="text-primary">Importação em andamento</strong>
                      </>
                    ) : (
                      <strong>Importação</strong>
                    )}
                  </div>
                  {statusBadge(job.status)}
                </div>

                {job.message && (
                  <p className="text-muted small mb-2">{job.message}</p>
                )}

                {isJobActive && (
                  <>
                    <div className="d-flex justify-content-between small text-muted mb-1">
                      <span>
                        Página {job.currentPage || 1}
                        {job.totalPages ? ` de ${job.totalPages}` : ''} ·{' '}
                        {formatNumber(job.totalImported)} registros importados
                      </span>
                      <span className="fw-semibold">
                        {jobPercent !== null ? `${jobPercent}%` : 'Inicializando...'}
                      </span>
                    </div>
                    <div
                      className="progress"
                      role="progressbar"
                      aria-label="Progresso da importação"
                      aria-valuenow={jobPercent || 0}
                      aria-valuemin="0"
                      aria-valuemax="100"
                      style={{ height: '12px' }}
                    >
                      <div
                        className="progress-bar progress-bar-striped progress-bar-animated"
                        style={{
                          width: `${jobPercent !== null ? jobPercent : 10}%`,
                          background: 'linear-gradient(45deg, rgb(19, 72, 156) 0%, rgba(50, 120, 220, 1) 100%)',
                        }}
                      ></div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Alerta de Sucesso */}
          {success && (
            <div
              className="alert alert-success alert-dismissible fade show mt-3"
              role="alert"
            >
              <i className="bi bi-check-circle-fill me-2"></i>
              {success}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccess('')}
              ></button>
            </div>
          )}
        </PageContent>
      </ResponsiveShadow>

      <LoadingModal loading={isStarting} message="Iniciando importação..." />

      <DataPreviewModal
        show={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false);
          setPreviewData(null);
        }}
        title="Visualização de Dados CATMAT"
        columns={previewColumns}
        data={previewData}
        isLoading={isPreviewLoading}
      />
      <DefaultModal
        show={showErrorModal}
        type="error"
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </Layout>
  );
}

export default CatmatImport;